import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { RouterStateUrl } from '../../router/router-state-serializer';
import { goToPreviousPage, loadPageWithPatientIdParameter, printPage, setGlobalError } from '../actions/app.actions';
import { selectGlobalError } from '../selectors/app.selector';

@Injectable()
export class AppNavigationEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private store = inject(Store);
  private titleService = inject(Title);
  private dialog = inject(MatDialog);

  private navigateToRoute(route: (string | number | null)[], replaceUrl?: boolean): void {
    if (replaceUrl) {
      this.router.navigate(route, { replaceUrl: true });
      return;
    }

    this.router.navigate(route);
  }

  navigateToErrorPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setGlobalError),
        concatLatestFrom(() => [this.store.select(selectGlobalError)]),
        tap(([, err]) => {
          if (err !== null) {
            this.dialog.closeAll();
            this.router.navigate(['error']);
          }
        })
      );
    },
    { dispatch: false }
  );

  updateTitle$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        tap((action: RouterNavigationAction<RouterStateUrl>) => {
          this.titleService.setTitle(action?.payload?.routerState?.title);
        })
      );
    },
    { dispatch: false }
  );

  loadPageWithPatientIdParameter$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loadPageWithPatientIdParameter),
        concatLatestFrom(() => [this.store.select(selectPatientId)]),
        tap(([action, patientId]) => {
          if (!action.route || action.route === '') {
            this.navigateToRoute(['patient', patientId], action.replaceUrl);
          } else if (action.route.split('/').length > 1) {
            const routes = action.route.split('/');
            const route = ['patient', patientId];
            routes.forEach(rte => {
              route.push(rte);
            });
            this.navigateToRoute(route, action.replaceUrl);
          } else {
            this.navigateToRoute(['patient', patientId, action.route], action.replaceUrl);
          }
        })
      );
    },
    { dispatch: false }
  );

  goToPreviousPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(goToPreviousPage),
        tap(() => {
          window.history.back();
        })
      );
    },
    { dispatch: false }
  );

  printPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(printPage),
        tap(() => {
          queueMicrotask(() => window.print());
        })
      );
    },
    { dispatch: false }
  );
}
